import {
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
  useCallback,
  useRef,
} from 'react';

import type { GlassVariant } from '@typings/glass';

import { useGlass } from './useGlass';

/** Elements a glass surface may render as. Semantics are the caller's call. */
type GlassElement = 'div' | 'section' | 'article' | 'aside' | 'header' | 'footer' | 'nav' | 'li';

interface GlassSurfaceProps extends Omit<HTMLAttributes<HTMLElement>, 'className' | 'style'> {
  readonly as?: GlassElement;
  readonly variant?: GlassVariant;
  readonly interactive?: boolean;
  readonly refract?: boolean;
  readonly className?: string | undefined;
  readonly style?: CSSProperties | undefined;
  readonly children?: ReactNode;
}

/**
 * A panel of glass. Cards, sections, sheets — anything that is not itself
 * a control. Controls use GlassButton or GlassLink so they get real
 * button and link semantics.
 */
export const GlassSurface = ({
  as: Tag = 'div',
  variant,
  interactive,
  refract,
  className,
  style,
  children,
  ...rest
}: GlassSurfaceProps) => {
  const ref = useRef<HTMLElement>(null);
  const glass = useGlass(ref, { variant, interactive, refract, className, style });

  /* A callback ref, because `Tag` is a union: no single RefObject type is
     assignable to the ref prop of every element it might render as. */
  const setRef = useCallback((node: HTMLElement | null) => {
    ref.current = node;
  }, []);

  return (
    <Tag
      {...rest}
      ref={setRef}
      className={glass.className}
      style={glass.style}
      data-glass-surface=""
    >
      {glass.filter}
      {children}
    </Tag>
  );
};
